import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Brain, Sparkles, FileText, Globe, TrendingUp, Wheat, Users, Clock, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const INTEL_TYPES = [
  { id: "country_intelligence", label: "Country Intelligence", icon: Globe, desc: "Comprehensive agricultural intelligence for a country" },
  { id: "corridor_briefs", label: "Corridor Brief", icon: TrendingUp, desc: "Trade corridor analysis between two countries" },
  { id: "crop_advisory", label: "Crop Advisory", icon: Wheat, desc: "Crop-specific cultivation advisory" },
  { id: "farmer_guidance", label: "Farmer Guidance", icon: Users, desc: "Personalized farmer guidance" },
  { id: "credit_narratives", label: "Credit Narrative", icon: FileText, desc: "Credit assessment narrative" },
  { id: "demand_notifications", label: "Demand Alert", icon: Sparkles, desc: "Commodity demand intelligence" },
];

const COUNTRIES = ["Tanzania", "Zambia", "Kenya", "Uganda", "Malawi", "Mozambique", "Rwanda"];
const CROPS = ["Maize", "Coffee", "Tea", "Wheat", "Soya", "Groundnuts", "Cotton", "Rice"];

export default function AIIntelligencePage() {
  const [reports, setReports] = useState([]);
  const [selectedType, setSelectedType] = useState("country_intelligence");
  const [country, setCountry] = useState("Tanzania");
  const [crop, setCrop] = useState("Maize");
  const [destination, setDestination] = useState("Kenya");
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
    const interval = setInterval(fetchReports, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchReports = async () => {
    try {
      const { data } = await api.get("/intelligence?limit=30");
      setReports(data.data || []);
      setLoading(false);
    } catch (err) {
      console.error("Failed to load reports:", err);
      setLoading(false);
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const context = { country, crop, origin: country, destination };
      if (selectedType === "farmer_guidance") {
        context.farmer_name = "Sample Farmer";
        context.crops = [crop];
      }
      if (selectedType === "credit_narratives") {
        context.score = 680;
        context.farmer_name = "Sample Farmer";
      }
      if (selectedType === "demand_notifications") {
        context.commodity = crop;
        context.region = "East Africa";
      }
      const { data } = await api.post("/intelligence/generate", { type: selectedType, context });
      toast.success("Intelligence generation started");
      // Poll for completion
      const pollInterval = setInterval(async () => {
        const { data: reports } = await api.get("/intelligence?limit=1");
        const latest = reports.data?.find(r => r.id === data.id);
        if (latest && latest.status !== "generating") {
          clearInterval(pollInterval);
          setGenerating(false);
          fetchReports();
          if (latest.status === "completed") {
            setSelectedReport(latest);
            toast.success("Intelligence report ready");
          } else {
            toast.error("Generation failed");
          }
        }
      }, 3000);
      // Timeout after 60s
      setTimeout(() => { clearInterval(pollInterval); setGenerating(false); }, 60000);
    } catch (err) {
      toast.error("Failed to generate intelligence");
      setGenerating(false);
    }
  };

  const statusIcon = (status) => {
    if (status === "completed") return <CheckCircle className="w-3 h-3 text-[#00ff41]" />;
    if (status === "generating") return <Loader2 className="w-3 h-3 text-[#0ea5e9] animate-spin" />;
    return <AlertCircle className="w-3 h-3 text-[#ff3b30]" />;
  };

  return (
    <div data-testid="ai-intelligence-page" className="p-4 md:p-6 space-y-4">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
          AI Intelligence Hub
        </h1>
        <p className="text-xs text-zinc-500 font-mono mt-1">GPT-5.2 powered agricultural intelligence generation</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Generator Panel */}
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
              <Brain className="w-3.5 h-3.5 text-[#0ea5e9]" />
              Generate Intelligence
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-4">
            {/* Type Selection */}
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono block mb-1.5">Report Type</label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger data-testid="intel-type-select" className="rounded-none border-zinc-800 bg-black text-xs font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-zinc-800 bg-zinc-950">
                  {INTEL_TYPES.map(t => (
                    <SelectItem key={t.id} value={t.id} className="text-xs font-mono">{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Country */}
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono block mb-1.5">Country</label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="intel-country-select" className="rounded-none border-zinc-800 bg-black text-xs font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-zinc-800 bg-zinc-950">
                  {COUNTRIES.map(c => (
                    <SelectItem key={c} value={c} className="text-xs font-mono">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Destination (for corridor) */}
            {selectedType === "corridor_briefs" && (
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono block mb-1.5">Destination</label>
                <Select value={destination} onValueChange={setDestination}>
                  <SelectTrigger className="rounded-none border-zinc-800 bg-black text-xs font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-zinc-800 bg-zinc-950">
                    {COUNTRIES.filter(c => c !== country).map(c => (
                      <SelectItem key={c} value={c} className="text-xs font-mono">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Crop */}
            {["crop_advisory", "farmer_guidance", "demand_notifications"].includes(selectedType) && (
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono block mb-1.5">Crop</label>
                <Select value={crop} onValueChange={setCrop}>
                  <SelectTrigger className="rounded-none border-zinc-800 bg-black text-xs font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-zinc-800 bg-zinc-950">
                    {CROPS.map(c => (
                      <SelectItem key={c} value={c} className="text-xs font-mono">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              data-testid="generate-intel-btn"
              className="w-full rounded-none bg-[#0ea5e9] text-black hover:bg-[#0284c7] font-mono text-xs uppercase tracking-wider h-10"
              onClick={generateReport}
              disabled={generating}
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generate Intelligence</>
              )}
            </Button>

            {/* Type Info */}
            <div className="border border-zinc-800 p-3 bg-black/50">
              <p className="text-[10px] text-zinc-500 font-mono">
                {INTEL_TYPES.find(t => t.id === selectedType)?.desc}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Report Viewer */}
        <Card className="lg:col-span-2 bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 text-[#0ea5e9]" />
              {selectedReport ? selectedReport.type?.replace(/_/g, " ") : "Intelligence Reports"}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {selectedReport?.content ? (
              <ScrollArea className="h-[500px]">
                <div className="prose prose-invert prose-sm max-w-none font-mono text-xs leading-relaxed text-zinc-300 whitespace-pre-wrap">
                  {selectedReport.content.content || selectedReport.content}
                </div>
              </ScrollArea>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {reports.map((report) => (
                    <button
                      key={report.id}
                      data-testid={`report-item-${report.id}`}
                      className="w-full text-left p-3 border border-zinc-800 hover:border-zinc-600 transition-colors duration-100"
                      onClick={() => setSelectedReport(report)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {statusIcon(report.status)}
                          <span className="text-xs font-mono text-zinc-300">{report.type?.replace(/_/g, " ")}</span>
                        </div>
                        <Badge variant="outline" className="rounded-none text-[9px] border-zinc-700 font-mono">
                          {report.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Clock className="w-3 h-3 text-zinc-600" />
                        <span className="text-[10px] text-zinc-600 font-mono">{new Date(report.created_at).toLocaleString()}</span>
                      </div>
                    </button>
                  ))}
                  {reports.length === 0 && (
                    <div className="text-center py-16 text-zinc-600 font-mono text-sm">
                      <Brain className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
                      <p>No intelligence reports yet</p>
                      <p className="text-[10px] mt-1">Generate your first report using the panel</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
            {selectedReport && (
              <Button
                data-testid="back-to-reports-btn"
                variant="ghost"
                size="sm"
                className="mt-3 text-zinc-500 text-xs font-mono"
                onClick={() => setSelectedReport(null)}
              >
                Back to reports
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
